import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    Inject,
    Injector,
    OnDestroy,
    OnInit,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { URLPattern } from 'urlpattern-polyfill';
import { ReviewOpsWorkspaceRouterState } from './workspace-tabs';

interface ReviewOpsWorkspacePane {
    appId: string;
    component: Type<any>;
    componentRef: ComponentRef<any>;
    detached: boolean;
    identity: string;
    tabId: string;
    url: string;
}

@Component({
    selector: 'reviewops-workspace-keepalive-outlet',
    template: '<div class="reviewops-workspace-keepalive-host"><ng-template #paneHost></ng-template></div>',
    styles: [`
        :host {
            display: block;
            width: 100%;
            height: 100%;
            min-width: 0;
            min-height: 0;
        }

        .reviewops-workspace-keepalive-host {
            position: relative;
            width: 100%;
            height: 100%;
            min-width: 0;
            min-height: 0;
            overflow: hidden;
            background: #f4f5f5;
        }
    `]
})
export class ReviewOpsWorkspaceKeepaliveOutletComponent implements OnInit, OnDestroy {
    @ViewChild('paneHost', { read: ViewContainerRef, static: true })
    private paneHost!: ViewContainerRef;

    private panes = new Map<string, ReviewOpsWorkspacePane>();
    private routeSubscription: any = null;
    private dropListener = (event: Event) => this.handleWorkspaceTabDropped(event as CustomEvent);
    private refreshListener = (event: Event) => this.handleWorkspaceTabRefresh(event as CustomEvent);
    private activeTabListener = () => this.scheduleActivePaneSync();
    private destroyed: boolean = false;
    private syncFrame?: number;

    constructor(
        @Inject(ActivatedRoute) private route: ActivatedRoute,
        @Inject(Router) private router: Router,
        @Inject(Injector) private injector: Injector,
        @Inject(ChangeDetectorRef) private ref: ChangeDetectorRef
    ) { }

    public ngOnInit() {
        this.routeSubscription = this.router.events.subscribe((event: any) => {
            if (event instanceof NavigationEnd) {
                this.syncActivePane(event.urlAfterRedirects || event.url || this.router.url);
            }
        });
        window.addEventListener('reviewops-workspace-tab-dropped', this.dropListener);
        window.addEventListener('reviewops-workspace-tab-refresh', this.refreshListener);
        window.addEventListener('reviewops-workspace-active-tab-changed', this.activeTabListener);
        this.syncActivePane(this.router.url || `${location.pathname}${location.search}${location.hash}`);
        this.scheduleActivePaneSync();
    }

    public ngOnDestroy() {
        this.destroyed = true;
        if (this.syncFrame) window.cancelAnimationFrame(this.syncFrame);
        if (this.routeSubscription && this.routeSubscription.unsubscribe) this.routeSubscription.unsubscribe();
        this.routeSubscription = null;
        window.removeEventListener('reviewops-workspace-tab-dropped', this.dropListener);
        window.removeEventListener('reviewops-workspace-tab-refresh', this.refreshListener);
        window.removeEventListener('reviewops-workspace-active-tab-changed', this.activeTabListener);
        Array.from(this.panes.keys()).forEach((tabId: string) => this.destroyPane(tabId));
        this.panes.clear();
    }

    private scheduleActivePaneSync() {
        if (this.destroyed || this.syncFrame) return;
        this.syncFrame = window.requestAnimationFrame(() => {
            this.syncFrame = undefined;
            this.syncActivePane(this.router.url || `${location.pathname}${location.search}${location.hash}`);
        });
    }

    private syncActivePane(url: string) {
        if (this.destroyed) return;
        const data: any = this.route.snapshot.data || {};
        const component = data.workspaceComponent as Type<any>;
        const appId = String(data.workspaceAppId || '');
        if (!component || !appId) return;

        const tabId = ReviewOpsWorkspaceRouterState.attachTabId() || 'default';
        const normalizedUrl = this.normalizeUrl(url);
        const params = this.routeParams(data, normalizedUrl);
        const identity = this.routeIdentity(appId, params);
        this.applyWizRoute(normalizedUrl, params);

        let pane = this.panes.get(tabId);
        if (!pane || pane.component !== component || pane.identity !== identity) {
            if (pane) this.destroyPane(tabId);
            pane = this.createPane(tabId, appId, component, identity, normalizedUrl);
            this.panes.set(tabId, pane);
        } else {
            pane.url = normalizedUrl;
        }

        this.panes.forEach((item: ReviewOpsWorkspacePane) => {
            this.setPaneActive(item, item.tabId === tabId);
        });
        this.ref.detectChanges();
    }

    private createPane(tabId: string, appId: string, component: Type<any>, identity: string, url: string) {
        const componentRef = this.paneHost.createComponent(component, { injector: this.injector });
        const pane: ReviewOpsWorkspacePane = {
            appId,
            component,
            componentRef,
            detached: false,
            identity,
            tabId,
            url
        };
        this.applyPaneBaseStyle(pane);
        return pane;
    }

    private setPaneActive(pane: ReviewOpsWorkspacePane, active: boolean) {
        const element = this.paneElement(pane);
        if (element) {
            element.style.visibility = active ? 'visible' : 'hidden';
            element.style.opacity = active ? '1' : '0';
            element.style.pointerEvents = active ? 'auto' : 'none';
            element.style.zIndex = active ? '1' : '0';
            element.setAttribute('aria-hidden', active ? 'false' : 'true');
        }

        if (!active && !pane.detached) {
            pane.detached = true;
            this.callPaneHook(pane, 'reviewopsWorkspaceDetached');
            return;
        }

        if (active && pane.detached) {
            pane.detached = false;
            this.restorePaneVisibilityState(pane);
        }
    }

    private applyPaneBaseStyle(pane: ReviewOpsWorkspacePane) {
        const element = this.paneElement(pane);
        if (!element) return;
        element.classList.add('reviewops-workspace-keepalive-pane');
        element.style.display = 'block';
        element.style.position = 'absolute';
        element.style.inset = '0';
        element.style.width = '100%';
        element.style.height = '100%';
        element.style.minWidth = '0';
        element.style.minHeight = '0';
        element.style.overflowX = 'hidden';
        element.style.overflowY = 'auto';
        element.style.background = '#f4f5f5';
        element.style.transition = 'opacity 120ms ease-out, filter 120ms ease-out';
        element.dataset.workspaceTabId = pane.tabId;
        element.dataset.workspaceAppId = pane.appId;
        this.setPaneActive(pane, true);
    }

    private destroyPane(tabId: string) {
        const pane = this.panes.get(tabId);
        if (!pane) return;
        try {
            pane.componentRef.destroy();
        } catch (e) { }
        this.panes.delete(tabId);
    }

    private handleWorkspaceTabDropped(event: CustomEvent) {
        const detail = event && event.detail ? event.detail : {};
        const tabId = String(detail.tabId || '');
        if (!tabId) return;
        this.destroyPane(tabId);
    }

    private handleWorkspaceTabRefresh(event: CustomEvent) {
        const detail = event && event.detail ? event.detail : {};
        const tabId = String(detail.tabId || '');
        if (!tabId) return;
        const active = tabId === (ReviewOpsWorkspaceRouterState.attachTabId() || 'default');
        this.destroyPane(tabId);
        if (active) this.scheduleActivePaneSync();
    }

    private callPaneHook(pane: ReviewOpsWorkspacePane, methodName: string) {
        const instance: any = pane.componentRef ? pane.componentRef.instance : null;
        const method = instance && instance[methodName];
        if (typeof method !== 'function') return;
        try {
            method.call(instance);
        } catch (e) { }
    }

    private restorePaneVisibilityState(pane: ReviewOpsWorkspacePane) {
        const instance: any = pane.componentRef ? pane.componentRef.instance : null;
        if (!instance) return;
        if ('workspaceDetached' in instance) instance.workspaceDetached = false;
        const method = instance.reviewopsWorkspaceVisibilityRestored;
        if (typeof method !== 'function') return;
        try {
            method.call(instance);
        } catch (e) { }
    }

    private paneElement(pane: ReviewOpsWorkspacePane) {
        const element = pane.componentRef && pane.componentRef.location ? pane.componentRef.location.nativeElement : null;
        return element && element.nodeType === 1 ? element as HTMLElement : null;
    }

    private routeIdentity(appId: string, params: any) {
        if (params && params.id) return `${appId}:id:${params.id}`;
        if (params && params.group_id) return `${appId}:group:${params.group_id}`;
        return appId || 'workspace-route';
    }

    private normalizeUrl(url: string) {
        return String(url || `${location.pathname}${location.search}${location.hash}` || '/');
    }

    private routeParams(data: any, url: string) {
        const params = Object.assign({}, this.route.snapshot.params || {});
        const workspacePath = String(data && data.workspacePath ? data.workspacePath : '');
        if (!workspacePath) return params;

        try {
            const pathname = String(url || '').split(/[?#]/)[0].replace(/^\/+/, '');
            const pattern = new URLPattern({ pathname: `/${workspacePath}` });
            const match = pattern.exec(`http://test/${pathname}`);
            const groups = match && match.pathname ? match.pathname.groups || {} : {};
            Object.keys(groups).forEach((key: string) => {
                if (groups[key]) params[key] = groups[key];
            });
        } catch (e) { }

        return params;
    }

    private applyWizRoute(url: string, params: any) {
        const routeInfo = (window as any).WizRoute || {};
        routeInfo.path = String(url || '').split(/[?#]/)[0].replace(/^\/+/, '');
        routeInfo.segment = Object.assign({}, params || {});
        (window as any).WizRoute = routeInfo;
    }
}
